// Trading journal: P&L over a period with the equity curve and drawdown, the numbers that matter
// (win rate, profit factor, expectancy in ₹ and R, payoff, drawdown, streaks), plain-English
// insights, a daily calendar, breakdowns by setup, habit, weekday, time of day and symbol, plan
// followed vs not, and the trade list. Trades are logged by hand, from a stock page (with
// MoneyHQ's plan at that moment) or imported from a Zerodha tradebook.
import { useEffect, useMemo, useRef, useState } from 'react';
import { FileUpIcon, LightbulbIcon, PencilIcon, PlusIcon, SearchIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { NSE } from '@planb/core';
import { drawEquity } from '@/charts/equity-chart';
import { useSearchParams } from '@/lib/nav';
import { useIndexStrip, useMarket } from '@/lib/client/use-market';
import { useResolvedTheme } from '@/lib/client/use-me';
import { SESSION_PARTS, SETUPS, setupLabel, tagLabel, TAGS, useTrades, type Bucket, type Stats, type Trade } from '@/lib/client/use-trades';
import { usePageTools } from '@/lib/desk/tools';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AppHeader, Page } from './app-header';
import { PaperTrades } from './paper-trades';
import { MomentumPortfolio } from './momentum-portfolio';
import { ForwardTest } from './forward-test';
import { DeskRecs } from './desk-recs';
import { carry, dateSeed, indexById } from './common';
import { Pills } from './market-ui';
import { TradeForm } from './trade-form';

const cur = NSE.currencySymbol;
const inr = (v: number, digits = 0) => `${v < 0 ? '−' : ''}${cur}${Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: digits })}`;
const signed = (v: number) => `${v > 0 ? '+' : ''}${inr(v)}`;
const tone = (v: number) => (v > 0 ? 'text-bull' : v < 0 ? 'text-bear' : 'text-muted-foreground');
const shortDate = (iso: string) => new Date(iso.length === 10 ? iso + 'T00:00:00' : iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

type Period = 'month' | '30d' | 'fy' | 'all';
const istToday = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function rangeFor(p: Period) {
  const t = istToday();
  const to = iso(t);
  if (p === 'month') return { from: iso(new Date(t.getFullYear(), t.getMonth(), 1)), to };
  if (p === '30d') return { from: iso(new Date(t.getTime() - 29 * 86_400_000)), to };
  if (p === 'fy') return { from: iso(new Date(t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1, 3, 1)), to };
  return { from: '', to: '' };
}

export function JournalApp() {
  const params = useSearchParams();
  const seed = Number(params.get('seed')) || dateSeed();
  const index = indexById(params.get('index'))?.id ?? 'FNO';
  const { session, status, mode, live, version, user, role } = useMarket({ index: 'NIFTY50', seed });
  const strip = useIndexStrip(session, live, version);
  const query = carry({ index, live, seed });
  const [period, setPeriod] = useState<Period>('30d');
  const range = useMemo(() => rangeFor(period), [period]);
  const { trades, stats, save, remove, importCsv } = useTrades(range);
  const [editing, setEditing] = useState<Trade | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  useEffect(() => { document.title = 'Journal · MoneyHQ'; }, []);

  // The desk's view of this page: the user's real-trade record for the period, the trades, and
  // pointers to the paper trades and the long-term portfolio (server tools).
  usePageTools({
    read_page: () => {
      if (!stats || !trades) return { page: 'journal', note: 'still loading' };
      const r2 = (x: number | null) => (x == null ? null : +x.toFixed(2));
      const top = (b: typeof stats.bySetup) => b.slice(0, 6);
      return {
        page: 'journal', period, from: range.from, to: range.to,
        realTrades: {
          note: 'The user\'s own real trades (logged or imported from Zerodha), with MoneyHQ\'s statistics. Review them as their record; never advise on real money.',
          stats: { trades: stats.trades, open: stats.open, wins: stats.wins, losses: stats.losses, winRatePct: r2(stats.winRate), net: r2(stats.net), fees: r2(stats.fees),
            profitFactor: r2(stats.profitFactor), expectancyPerTrade: r2(stats.expectancy), expectancyR: r2(stats.expectancyR), avgWin: r2(stats.avgWin), avgLoss: r2(stats.avgLoss),
            maxDrawdown: r2(stats.maxDrawdown), bestDay: stats.bestDay, worstDay: stats.worstDay, longestWinStreak: stats.longestWin, longestLossStreak: stats.longestLoss },
          bySetup: top(stats.bySetup), bySide: stats.bySide, byWeekday: stats.byWeekday, plan: stats.plan,
          recent: trades.slice(0, 15).map((t) => ({ symbol: t.symbol, side: t.side, qty: t.qty, entry: t.entry, exit: t.exit, entryAt: t.entryAt, net: r2(t.net), r: r2(t.r), setup: t.setup || null, note: t.note || null })),
        },
        alsoHere: 'The user\'s paper trades and long-term momentum portfolio are on this page too.',
      };
    },
  });
  const openNew = () => { setEditing(null); setFormOpen(true); };
  const empty = trades !== null && trades.length === 0;

  return (
    <>
      <AppHeader section="journal" mode={mode} status={status} user={user} role={role} query={query} strip={strip} />
      <Page>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-[26px] font-semibold">Trading journal</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">Your real results, what works for you, and the habits that cost money.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="h-9 rounded-full px-4" onClick={() => setImportOpen(true)}><FileUpIcon />Import tradebook</Button>
            <Button className="h-9 rounded-full px-4" onClick={openNew}><PlusIcon />Log trade</Button>
          </div>
        </div>

        <PaperTrades />
        <DeskRecs />
        <MomentumPortfolio />
        {role === 'admin' && <ForwardTest />}

        {trades === null ? <p className="py-16 text-center text-muted-foreground">Loading…</p> : empty ? (
          <EmptyJournal onImport={() => setImportOpen(true)} onLog={openNew} />
        ) : (
          <>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Pills label="Period" value={period} onChange={setPeriod}
                options={[{ id: 'month', label: 'This month' }, { id: '30d', label: 'Last 30 days' }, { id: 'fy', label: 'This FY' }, { id: 'all', label: 'All time' }]} />
              {range.from && <span className="num text-xs text-muted-foreground">{shortDate(range.from)} – {shortDate(range.to)}</span>}
            </div>
            {stats && <Overview st={stats} />}
            <TradeList trades={trades} onEdit={(t) => { setEditing(t); setFormOpen(true); }}
              onDelete={async (t) => { if (confirm(`Delete the ${t.symbol} trade?`) && await remove(t.id)) toast.success('Trade deleted'); }} />
          </>
        )}
      </Page>
      <TradeForm open={formOpen} onOpenChange={setFormOpen} trade={editing}
        onSave={async (t) => { const e = await save(t, editing?.id); if (!e) toast.success(editing ? 'Trade updated' : 'Trade logged'); return e; }} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onImport={importCsv} />
    </>
  );
}

function EmptyJournal({ onImport, onLog }: { onImport: () => void; onLog: () => void }) {
  return (
    <Card className="mt-6 items-center gap-0 px-6 py-14 text-center">
      <LightbulbIcon className="size-8 text-primary" />
      <h2 className="mt-3 text-xl font-semibold">Start your journal</h2>
      <p className="mt-1.5 max-w-lg text-sm text-muted-foreground">
        Import your Zerodha tradebook to see your P&amp;L, win rate and the habits that cost you, or log trades as you take them.
        Log from a stock page and MoneyHQ keeps its plan at that moment, so you can see whether following the plan pays.
      </p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        <Button className="rounded-full" onClick={onImport}><FileUpIcon />Import tradebook</Button>
        <Button variant="outline" className="rounded-full" onClick={onLog}><PlusIcon />Log a trade</Button>
      </div>
    </Card>
  );
}

function Overview({ st }: { st: Stats }) {
  const ref = useRef<SVGSVGElement>(null);
  const theme = useResolvedTheme();
  useEffect(() => { if (ref.current) drawEquity(ref.current, st.equity, (v) => inr(v)); }, [st, theme]);
  const long = st.bySide.find((b) => b.key === 'long'), short = st.bySide.find((b) => b.key === 'short');
  const kpis: [string, React.ReactNode, React.ReactNode?][] = [
    ['Win rate', `${st.winRate.toFixed(0)}%`, `${st.wins} of ${st.trades} trades`],
    ['Profit factor', st.profitFactor == null ? (st.wins ? '∞' : '—') : st.profitFactor.toFixed(2), 'won ÷ lost'],
    ['Expectancy', <span className={tone(st.expectancy)}>{signed(st.expectancy)}</span>, st.expectancyR == null ? 'per trade' : `per trade · ${st.expectancyR >= 0 ? '+' : ''}${st.expectancyR.toFixed(2)}R`],
    ['Avg win / loss', <><span className="text-bull">{inr(st.avgWin)}</span> / <span className="text-bear">{inr(Math.abs(st.avgLoss))}</span></>, st.payoff == null ? '' : `${st.payoff.toFixed(2)}× payoff`],
    ['Max drawdown', <span className="text-bear">{inr(st.maxDrawdown)}</span>, st.maxDrawdownDate ? `bottomed ${shortDate(st.maxDrawdownDate)}` : 'none'],
    ['Best day', st.bestDay ? <span className="text-bull">{signed(st.bestDay.net)}</span> : '—', st.bestDay ? shortDate(st.bestDay.date) : ''],
    ['Worst day', st.worstDay ? <span className="text-bear">{signed(st.worstDay.net)}</span> : '—', st.worstDay ? shortDate(st.worstDay.date) : ''],
    ['Current streak', st.streak.kind ? <span className={st.streak.kind === 'win' ? 'text-bull' : 'text-bear'}>{st.streak.n} {st.streak.kind === 'win' ? 'wins' : st.streak.n > 1 ? 'losses' : 'loss'}</span> : '—', `best run ${st.longestWin} · worst ${st.longestLoss}`],
  ];
  return (
    <>
      <Card className="mt-4 gap-0 py-0">
        <div className="flex flex-wrap items-start justify-between gap-4 px-5 pt-5">
          <div>
            <div className="text-[12.5px] font-medium text-muted-foreground">Net P&amp;L</div>
            <div className={cn('num text-[34px] leading-tight font-semibold', tone(st.net))}>{signed(st.net)}</div>
            <div className="text-[12.5px] text-muted-foreground">{st.trades} trades · {st.wins} wins · {inr(st.fees)} charges{st.open ? ` · ${st.open} open` : ''}</div>
          </div>
          <div className="flex gap-2">
            {[long, short].filter(Boolean).map((b) => (
              <div key={b!.key} className="rounded-lg border bg-tint px-3 py-2 text-right">
                <div className="text-[11.5px] text-muted-foreground capitalize">{b!.key} · {b!.trades} trades</div>
                <div className={cn('num text-sm font-semibold', tone(b!.net))}>{signed(b!.net)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="px-2 pt-2"><svg ref={ref} className="dt-chart" role="img" aria-label="Cumulative P&L with drawdown" /></div>
        <p className="flex gap-4 px-5 pb-3 text-[11.5px] text-muted-foreground">
          <span><i className="mr-1 inline-block h-0.5 w-4 bg-bull align-middle" />Cumulative P&amp;L</span>
          <span><i className="mr-1 inline-block h-2 w-4 bg-bear/20 align-middle" />Drawdown from the peak</span>
        </p>
        <div className="grid grid-cols-2 border-t sm:grid-cols-4 xl:grid-cols-8">
          {kpis.map(([k, v, note]) => (
            <div key={k} className="border-r border-b px-4 py-3 last:border-r-0 xl:border-b-0">
              <div className="text-[11.5px] text-muted-foreground">{k}</div>
              <div className="num mt-0.5 text-[16px] font-semibold text-ink">{v}</div>
              {note && <div className="text-[11px] text-muted-foreground">{note}</div>}
            </div>
          ))}
        </div>
      </Card>

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
        <Card className="gap-0 px-5 py-4">
          <h2 className="text-[15px] font-semibold">Daily P&amp;L</h2>
          <Calendar days={st.days} />
          <p className="mt-2 text-[12px] text-muted-foreground">{st.greenDays} green days · {st.redDays} red days</p>
        </Card>
        <Insights st={st} />
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Breakdown title="Plan followed vs not" note="Trades tagged “Followed plan” against the rest." rows={st.plan} label={(k) => (k === 'followed' ? 'Followed the plan' : 'Everything else')} />
        <Breakdown title="By time of day" note="When you entered: the board’s session band." rows={st.byTime} label={(k) => SESSION_PARTS[k] ?? k} />
        <Breakdown title="By setup" rows={st.bySetup} label={setupLabel} />
        <Breakdown title="Habits & mistakes" rows={st.byTag} label={tagLabel} empty="Tag trades with how they went (FOMO, revenge, followed plan…) to see what each habit costs." />
        <Breakdown title="By weekday" rows={st.byWeekday} label={(k) => k} />
        <Breakdown title="Top symbols" rows={[...st.bySymbol.slice(0, 5), ...st.bySymbol.slice(5).slice(-3)]} label={(k) => k} />
      </div>
    </>
  );
}

/** Weeks as columns, Monday–Friday as rows; colour by the day's net P&L. */
function Calendar({ days }: { days: Stats['days'] }) {
  if (!days.length) return <p className="py-6 text-sm text-muted-foreground">No closed trades in this period.</p>;
  const by = new Map(days.map((d) => [d.date, d]));
  const max = Math.max(...days.map((d) => Math.abs(d.net))) || 1;
  const first = new Date(days[0].date + 'T00:00:00'), last = new Date(days[days.length - 1].date + 'T00:00:00');
  const start = new Date(first.getTime() - ((first.getDay() + 6) % 7) * 86_400_000);
  const weeks: Date[][] = [];
  for (let w = new Date(start); w <= last; w = new Date(w.getTime() + 7 * 86_400_000)) {
    weeks.push([0, 1, 2, 3, 4].map((i) => new Date(w.getTime() + i * 86_400_000)));
  }
  const shown = weeks.slice(-26);
  return (
    <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1">
      <div className="grid grid-rows-5 gap-1 pt-4 text-[10px] text-muted-foreground">{['Mon', '', 'Wed', '', 'Fri'].map((d, i) => <span key={i} className="h-4 leading-4">{d}</span>)}</div>
      {shown.map((wk, i) => (
        <div key={i} className="grid gap-1">
          <span className="h-3 text-[10px] leading-3 whitespace-nowrap text-muted-foreground">{wk[0].getDate() <= 7 ? wk[0].toLocaleDateString('en-IN', { month: 'short' }) : ''}</span>
          {wk.map((d) => {
            const day = by.get(iso(d));
            const a = day ? 0.25 + 0.75 * Math.min(1, Math.abs(day.net) / max) : 0;
            return (
              <span key={iso(d)} title={day ? `${shortDate(iso(d))}: ${signed(day.net)} · ${day.trades} trade${day.trades > 1 ? 's' : ''}` : shortDate(iso(d))}
                className={cn('size-4 rounded-[4px] border border-border/60', !day && 'bg-tint')}
                style={day ? { background: `color-mix(in oklab, var(${day.net >= 0 ? '--bull' : '--bear'}) ${Math.round(a * 100)}%, transparent)` } : undefined} />
            );
          })}
        </div>
      ))}
    </div>
  );
}

function Breakdown({ title, note, rows, label, empty }: { title: string; note?: string; rows: Bucket[]; label: (k: string) => string; empty?: string }) {
  const max = Math.max(...rows.map((r) => Math.abs(r.net))) || 1;
  return (
    <Card className="gap-0 px-5 py-4">
      <h2 className="text-[15px] font-semibold">{title}</h2>
      {note && <p className="text-[12px] text-muted-foreground">{note}</p>}
      {rows.length === 0 ? <p className="py-4 text-[13px] text-muted-foreground">{empty ?? 'No trades yet.'}</p> : (
        <div className="mt-3 grid gap-2.5">
          {rows.map((r) => (
            <div key={r.key} className="grid grid-cols-[minmax(0,150px)_1fr_auto] items-center gap-3">
              <div className="min-w-0">
                <div className="truncate text-[13px] font-medium text-ink">{label(r.key)}</div>
                <div className="text-[11px] text-muted-foreground">{r.trades} trade{r.trades > 1 ? 's' : ''} · {r.winRate.toFixed(0)}% wins{r.avgR != null ? ` · ${r.avgR >= 0 ? '+' : ''}${r.avgR.toFixed(2)}R` : ''}</div>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-tint">
                <div className={cn('h-full rounded-full', r.net >= 0 ? 'bg-bull' : 'bg-bear')} style={{ width: `${Math.max(3, (Math.abs(r.net) / max) * 100)}%` }} />
              </div>
              <div className={cn('num w-24 text-right text-[13px] font-semibold', tone(r.net))}>{signed(r.net)}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** A few plain-English observations from the numbers (educational, not advice). */
function Insights({ st }: { st: Stats }) {
  const out: string[] = [];
  const [fol, rest] = [st.plan.find((b) => b.key === 'followed'), st.plan.find((b) => b.key === 'not-followed')];
  if (fol && rest && fol.trades >= 2 && rest.trades >= 2) {
    const a = fol.net / fol.trades, b = rest.net / rest.trades;
    out.push(`When you followed your plan you averaged ${signed(Math.round(a))} a trade, against ${signed(Math.round(b))} otherwise.`);
  }
  const bad = st.byTag.filter((b) => !TAGS.find((t) => t.id === b.key)?.good && b.net < 0).sort((p, q) => p.net - q.net)[0];
  if (bad) out.push(`“${tagLabel(bad.key)}” trades cost ${inr(Math.abs(bad.net))} over ${bad.trades} trade${bad.trades > 1 ? 's' : ''}.`);
  const parts = st.byTime.filter((b) => b.trades >= 2).sort((p, q) => q.net / q.trades - p.net / p.trades);
  if (parts.length >= 2) {
    const best = parts[0], worst = parts[parts.length - 1];
    if (worst.net < 0) out.push(`Your entries in the ${(SESSION_PARTS[worst.key] ?? worst.key).split(' ')[0].toLowerCase()} session lost ${inr(Math.abs(worst.net))}; ${(SESSION_PARTS[best.key] ?? best.key).split(' ')[0].toLowerCase()} entries did best.`);
  }
  if (st.gross > 0 && st.fees / st.gross > 0.3) out.push(`Charges took ${Math.round((st.fees / st.gross) * 100)}% of your gross profit: fewer, larger-edge trades keep more of it.`);
  if (st.payoff != null && st.payoff < 1 && st.winRate < 60) out.push(`Your average loss is bigger than your average win (${st.payoff.toFixed(2)}× payoff) with a ${st.winRate.toFixed(0)}% win rate, a combination that loses over time.`);
  if (st.expectancyR != null) out.push(`On trades with a planned stop you made ${st.expectancyR >= 0 ? '+' : ''}${st.expectancyR.toFixed(2)}R per trade on average.`);
  const setups = st.bySetup.filter((b) => b.trades >= 3 && b.key !== 'untagged');
  if (setups.length >= 2) out.push(`Best setup so far: ${setupLabel(setups[0].key)} (${signed(setups[0].net)} over ${setups[0].trades} trades).`);
  return (
    <Card className="gap-0 px-5 py-4">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold"><LightbulbIcon className="size-4 text-primary" />Insights</h2>
      {out.length ? (
        <ul className="mt-2 grid gap-2">
          {out.slice(0, 5).map((t) => <li key={t} className="rounded-lg bg-tint px-3 py-2 text-[13px] leading-snug text-ink">{t}</li>)}
        </ul>
      ) : <p className="mt-2 text-[13px] text-muted-foreground">Log a few more trades with setups and tags, and patterns will show up here.</p>}
      <p className="mt-3 text-[11px] text-muted-foreground">From your own trades, for reflection. Not investment advice.</p>
    </Card>
  );
}

function TradeList({ trades, onEdit, onDelete }: { trades: Trade[]; onEdit: (t: Trade) => void; onDelete: (t: Trade) => void }) {
  const [side, setSide] = useState<'all' | 'long' | 'short'>('all');
  const [setup, setSetup] = useState('all');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(50);
  const rows = trades.filter((t) => (side === 'all' || t.side === side) && (setup === 'all' || t.setup === setup) && (!q || t.symbol.includes(q.toUpperCase())));
  return (
    <Card className="mt-4 gap-0 py-0">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
        <h2 className="text-[15px] font-semibold">Trades <span className="font-normal text-muted-foreground">· {rows.length}</span></h2>
        <div className="flex flex-wrap items-center gap-2">
          <Pills size="sm" label="Side" value={side} onChange={setSide} options={[{ id: 'all', label: 'All' }, { id: 'long', label: 'Long' }, { id: 'short', label: 'Short' }]} />
          <Select value={setup} onValueChange={setSetup}>
            <SelectTrigger className="h-8 w-40" aria-label="Setup"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All setups</SelectItem>{SETUPS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <label className="relative"><SearchIcon className="absolute top-2 left-2.5 size-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Symbol" className="h-8 w-32 pl-8" aria-label="Filter by symbol" /></label>
        </div>
      </div>
      <Table>
        <TableHeader><TableRow className="hover:bg-transparent">
          <TableHead className="pl-5">Date</TableHead><TableHead>Symbol</TableHead><TableHead className="text-right">Qty</TableHead>
          <TableHead className="text-right">Entry → exit</TableHead><TableHead className="text-right">P&amp;L</TableHead>
          <TableHead className="hidden text-right md:table-cell">R</TableHead><TableHead className="hidden md:table-cell">Setup</TableHead>
          <TableHead className="hidden lg:table-cell">Tags</TableHead><TableHead className="pr-5" />
        </TableRow></TableHeader>
        <TableBody>
          {rows.slice(0, limit).map((t) => (
            <TableRow key={t.id}>
              <TableCell className="py-2.5 pl-5">
                <div className="num text-[13px] text-ink">{shortDate(t.entryAt)}</div>
                <div className="num text-[11px] text-muted-foreground">{new Date(t.entryAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
              </TableCell>
              <TableCell>
                <div className="font-medium text-ink">{t.symbol}{t.plan && <span className="ml-1.5 rounded bg-brand-soft px-1 text-[10px] font-semibold text-brand-strong" title="Logged with MoneyHQ's plan">PLAN</span>}</div>
                <div className={cn('text-[11px] font-medium', t.side === 'long' ? 'text-bull' : 'text-bear')}>{t.side === 'long' ? 'Long' : 'Short'}{t.segment === 'FO' ? ' · F&O' : ''}</div>
              </TableCell>
              <TableCell className="num text-right text-[13px]">{t.qty.toLocaleString('en-IN')}</TableCell>
              <TableCell className="num text-right text-[13px]">{NSE.formatPrice(t.entry)} → {t.exit == null ? <span className="text-muted-foreground">open</span> : NSE.formatPrice(t.exit)}</TableCell>
              <TableCell className={cn('num text-right text-[13px] font-semibold', t.exit == null ? 'text-muted-foreground' : tone(t.net))}>{t.exit == null ? '—' : signed(t.net)}</TableCell>
              <TableCell className={cn('num hidden text-right text-[13px] md:table-cell', t.r == null ? 'text-muted-foreground' : tone(t.r))}>{t.r == null ? '—' : `${t.r >= 0 ? '+' : ''}${t.r.toFixed(2)}`}</TableCell>
              <TableCell className="hidden text-[13px] md:table-cell">{t.setup ? setupLabel(t.setup) : <span className="text-muted-foreground">—</span>}</TableCell>
              <TableCell className="hidden lg:table-cell">
                <div className="flex flex-wrap gap-1">{t.tags.map((g) => {
                  const good = TAGS.find((x) => x.id === g)?.good;
                  return <span key={g} className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', good ? 'bg-bull-soft text-bull-strong' : 'bg-bear-soft text-bear-strong')}>{tagLabel(g)}</span>;
                })}</div>
              </TableCell>
              <TableCell className="pr-5 text-right whitespace-nowrap">
                <Button variant="ghost" size="icon" className="size-8" aria-label={`Edit ${t.symbol} trade`} onClick={() => onEdit(t)}><PencilIcon /></Button>
                <Button variant="ghost" size="icon" className="size-8 text-bear hover:text-bear" aria-label={`Delete ${t.symbol} trade`} onClick={() => onDelete(t)}><Trash2Icon /></Button>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={9} className="py-10 text-center text-sm text-muted-foreground">No trades match.</TableCell></TableRow>}
        </TableBody>
      </Table>
      {rows.length > limit && <div className="border-t p-3 text-center"><Button variant="outline" className="rounded-full" onClick={() => setLimit((l) => l + 100)}>Show more</Button></div>}
    </Card>
  );
}

function ImportDialog({ open, onOpenChange, onImport }: {
  open: boolean; onOpenChange: (o: boolean) => void;
  onImport: (text: string) => Promise<{ added: number; skipped: number; open: number; warnings: string[] } | string>;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  useEffect(() => { if (open) setResult(null); }, [open]);
  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 3_500_000) { setResult('That file is too large; export a shorter period.'); return; }
    setBusy(true);
    const r = await onImport(await file.text());
    setBusy(false);
    if (typeof r === 'string') { setResult(r); return; }
    const msg = `Imported ${r.added} trade${r.added === 1 ? '' : 's'}${r.open ? ` (${r.open} still open)` : ''}${r.skipped ? `, ${r.skipped} already in your journal` : ''}.`;
    toast.success(msg);
    setResult(msg + (r.warnings.length ? ` ${r.warnings.length} row${r.warnings.length > 1 ? 's' : ''} couldn’t be read.` : ''));
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Import a Zerodha tradebook</DialogTitle>
          <DialogDescription>Buys and sells are paired into round trips (first in, first out). Importing the same file again won’t add duplicates.</DialogDescription>
        </DialogHeader>
        <ol className="grid list-decimal gap-1.5 pl-5 text-[13px] text-ink">
          <li>Open <b>Zerodha Console</b> → Reports → <b>Tradebook</b>.</li>
          <li>Pick Equity or F&amp;O and a date range that covers whole positions, then download the <b>CSV</b>.</li>
          <li>Choose that file below.</li>
        </ol>
        <label className={cn('mt-1 grid cursor-pointer place-items-center rounded-xl border-2 border-dashed px-4 py-8 text-center hover:border-primary/60', busy && 'pointer-events-none opacity-60')}>
          <FileUpIcon className="size-6 text-primary" />
          <span className="mt-2 text-sm font-medium text-ink">{busy ? 'Importing…' : 'Choose tradebook CSV'}</span>
          <span className="text-xs text-muted-foreground">The file is read by MoneyHQ only to create your trades.</span>
          <input type="file" accept=".csv,text/csv" className="sr-only" onChange={pick} />
        </label>
        <p className="text-[12px] text-muted-foreground">Tradebooks don’t include brokerage and taxes, so charges start at ₹0; edit a trade to add them.</p>
        {result && <p role="status" className="rounded-lg bg-tint px-3 py-2 text-[13px] text-ink">{result}</p>}
      </DialogContent>
    </Dialog>
  );
}
